import { useMutation } from '@tanstack/react-query';
import { SKIP_FORBIDDEN_HEADER } from '@sentinel/api-client';
import { useApi } from '../../lib/api';

/**
 * mfa.setup.tsx fires the enroll mutation from a mount effect, so a 403 here
 * arrives without the user having done anything. The org-policy refusal
 * (crates/sentinel-app/src/auth/mfa_enroll.rs) is already reported inline as
 * `totp_start_failed`; skip the global handler so it is not also a toast.
 */
const ENROLL_HEADERS = { [SKIP_FORBIDDEN_HEADER]: '1' } as const;

export interface TotpEnrollResult {
  secret: string;
  provisioning_uri: string;
}

export interface TotpConfirmResult {
  recovery_codes: string[];
  /** Present only in the setup_token flow. */
  access_token?: string;
  refresh_token?: string;
  family_id?: string;
}

interface ErrorBody {
  error?: string;
  message?: string;
}

export interface TotpError extends Error {
  status?: number;
  body?: ErrorBody;
}

function toError(stage: string, response: Response | undefined, error: unknown): TotpError {
  const e = new Error(stage) as TotpError;
  if (response) e.status = response.status;
  e.body = (error ?? {}) as ErrorBody;
  return e;
}

export interface UseTotpOptions {
  /** When true, drive the unauthenticated `setup_token` flow. */
  setupToken?: string | null;
}

/**
 * TOTP enrollment mutation pair.
 *
 * - In authenticated mode (`setupToken` falsy), uses `/auth/mfa/enroll` and
 *   `/auth/mfa/confirm-enroll`.
 * - In setup-token mode, uses `/auth/mfa/setup-enroll` and
 *   `/auth/mfa/setup-confirm-enroll`. The confirm response also returns tokens
 *   (cookies are set by the server) so the user is logged in afterward.
 */
export function useTotpEnroll(opts: UseTotpOptions = {}) {
  const api = useApi();
  const { setupToken } = opts;

  const enroll = useMutation<TotpEnrollResult, TotpError, void>({
    mutationFn: async () => {
      if (setupToken) {
        const { data, error, response } = await api.POST('/auth/mfa/setup-enroll', {
          body: { setup_token: setupToken },
          headers: ENROLL_HEADERS,
        });
        if (error) throw toError('mfa_setup_enroll_failed', response, error);
        return data as TotpEnrollResult;
      }
      const { data, error, response } = await api.POST('/auth/mfa/enroll', {
        headers: ENROLL_HEADERS,
      });
      if (error) throw toError('mfa_enroll_failed', response, error);
      return data as TotpEnrollResult;
    },
  });

  const confirm = useMutation<TotpConfirmResult, TotpError, { code: string }>({
    mutationFn: async ({ code }) => {
      if (setupToken) {
        const { data, error, response } = await api.POST('/auth/mfa/setup-confirm-enroll', {
          body: { setup_token: setupToken, code },
        });
        if (error) throw toError('mfa_setup_confirm_failed', response, error);
        return data as TotpConfirmResult;
      }
      const { data, error, response } = await api.POST('/auth/mfa/confirm-enroll', {
        body: { code },
      });
      if (error) throw toError('mfa_confirm_failed', response, error);
      return data as TotpConfirmResult;
    },
  });

  return { enroll, confirm };
}
